import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keeps a text field's value in local state so caret position is stable while typing,
 * while still syncing from the parent when the parent value changes externally.
 */
export function useSelfControlledFieldValue(
  valueFromParent: string,
  onChange: (value: string) => void
): [string, (next: string) => void] {
  const [localValue, setLocalValue] = useState(valueFromParent);
  const lastEmittedRef = useRef(valueFromParent);

  useEffect(() => {
    if (valueFromParent !== lastEmittedRef.current) {
      setLocalValue(valueFromParent);
      lastEmittedRef.current = valueFromParent;
    }
  }, [valueFromParent]);

  const handleChange = useCallback(
    (next: string) => {
      setLocalValue(next);
      lastEmittedRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  return [localValue, handleChange];
}
